/**
 * signature-pad.tsx — Khung vẽ chữ ký tay (canvas, chuột / cảm ứng / bút). Xuất PNG nền trong suốt.
 * Không thư viện ngoài. `touch-action: none` để vuốt trên điện thoại không cuộn trang khi đang ký.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { hasInk } from '@/pages/platform/contract/contract-helpers'

export interface SignaturePadHandle {
  clear: () => void
  /** dataURL PNG nền trong suốt, hoặc null nếu chưa có nét. */
  toDataUrl: () => string | null
}

interface Props {
  /** Gọi khi trạng thái "đã có nét vẽ" đổi. */
  onInkChange?: (hasInk: boolean) => void
  className?: string
  disabled?: boolean
}

const W = 520
const H = 200

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad({ onInkChange, className, disabled }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const inked = useRef(false)

  const ctx = useCallback(() => canvasRef.current?.getContext('2d') ?? null, [])

  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const scale = Math.min(2, window.devicePixelRatio || 1)
    c.width = W * scale
    c.height = H * scale
    const g = c.getContext('2d')
    if (g) {
      g.scale(scale, scale)
      g.lineWidth = 2.4
      g.lineCap = 'round'
      g.lineJoin = 'round'
      g.strokeStyle = '#111827'
    }
  }, [])

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H }
  }

  const onInkRef = useRef(onInkChange)
  useEffect(() => { onInkRef.current = onInkChange })
  const setInk = useCallback((v: boolean) => {
    if (inked.current === v) return
    inked.current = v
    onInkRef.current?.(v)
  }, [])

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return
    e.currentTarget.setPointerCapture(e.pointerId)
    drawing.current = true
    const p = point(e)
    last.current = p
    const g = ctx()
    if (g) { g.beginPath(); g.arc(p.x, p.y, 1.2, 0, Math.PI * 2); g.fillStyle = '#111827'; g.fill() }
  }
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return
    const p = point(e)
    const g = ctx()
    if (!g) return
    g.beginPath()
    g.moveTo(last.current.x, last.current.y)
    g.lineTo(p.x, p.y)
    g.stroke()
    last.current = p
    setInk(true)
  }
  const up = () => { drawing.current = false; last.current = null }

  useImperativeHandle(ref, () => ({
    clear() {
      const c = canvasRef.current
      const g = ctx()
      if (c && g) {
        g.save()
        g.setTransform(1, 0, 0, 1, 0, 0)
        g.clearRect(0, 0, c.width, c.height)
        g.restore()
      }
      setInk(false)
    },
    toDataUrl() {
      const c = canvasRef.current
      const g = ctx()
      if (!c || !g) return null
      if (!hasInk(g.getImageData(0, 0, c.width, c.height).data)) return null
      // Xuất đúng khung W×H (không phụ thuộc devicePixelRatio) để server kiểm kích thước ổn định.
      const out = document.createElement('canvas')
      out.width = W
      out.height = H
      out.getContext('2d')?.drawImage(c, 0, 0, W, H)
      return out.toDataURL('image/png')
    },
  }), [ctx, setInk])

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label="Khung vẽ chữ ký"
      className={className ?? 'h-[200px] w-full touch-none rounded-md border bg-white'}
      style={{ touchAction: 'none' }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onPointerLeave={up}
    />
  )
})
